import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, Upload, X, CheckCircle2, Mail, ShieldCheck, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Turnstile } from "@/components/Turnstile";

interface ReviewFormProps {
  defaultProduct?: "brainCharge";
  lockProduct?: boolean;
  embedded?: boolean;
}

export function ReviewForm({ defaultProduct, lockProduct = false, embedded = false }: ReviewFormProps) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    product: defaultProduct ?? "",
    title: "",
    review: "",
  });

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const MAX_PHOTOS = 5;
  const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - photoFiles.length;
    const accepted: File[] = [];
    for (const f of files.slice(0, remaining)) {
      if (!f.type.startsWith("image/")) {
        toast({ title: "Invalid file", description: `${f.name} is not an image.`, variant: "destructive" });
        continue;
      }
      if (f.size > MAX_PHOTO_SIZE) {
        toast({ title: "File too large", description: `${f.name} exceeds 5MB.`, variant: "destructive" });
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) {
      setPhotoFiles((prev) => [...prev, ...accepted]);
      setPhotoPreviews((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
    }
    e.target.value = "";
  };

  const removePhoto = (index: number) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadPhotos = async (): Promise<string[]> => {
    if (!photoFiles.length) return [];
    setUploading(true);
    const urls: string[] = [];
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in to attach photos.");
      for (const file of photoFiles) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("review-photos")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("review-photos").getPublicUrl(path);
        urls.push(pub.publicUrl);
      }
      return urls;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const photo_urls = await uploadPhotos();

      const { data, error } = await supabase.functions.invoke("submit-review", {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          product_key: formData.product && formData.product !== "general" ? formData.product : null,
          rating,
          title: formData.title.trim(),
          review_text: formData.review.trim(),
          photo_urls,
          turnstileToken,
        },
      });

      if (error) throw error;

      // Reset form + show confirmation screen
      setFormData({ name: "", email: "", product: defaultProduct ?? "", title: "", review: "" });
      setRating(5);
      photoPreviews.forEach((u) => URL.revokeObjectURL(u));
      setPhotoFiles([]);
      setPhotoPreviews([]);
      setTurnstileToken("");
      setSubmitted(true);
    } catch (error: any) {
      console.error("Error submitting review:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const success = (
        <CardContent className="p-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle2 className="h-9 w-9 text-primary" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">Thanks for your review!</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your submission was received successfully. Here's what happens next:
            </p>
          </div>
          <ol className="text-left max-w-md mx-auto space-y-4">
            <li className="flex gap-3">
              <Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Check your inbox</p>
                <p className="text-sm text-muted-foreground">
                  We just emailed you a verification link. Click it within 24 hours to confirm it's really you.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Our team reviews it</p>
                <p className="text-sm text-muted-foreground">
                  Every review is moderated to keep things genuine. Approval typically takes <strong>1–2 business days</strong>.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">It goes live</p>
                <p className="text-sm text-muted-foreground">
                  Once approved, your review (and any photos) will appear in the reviews section for everyone to see.
                </p>
              </div>
            </li>
          </ol>
          <Button variant="outline" onClick={() => setSubmitted(false)}>
            Submit another review
          </Button>
        </CardContent>
    );
    return embedded ? success : <Card className="border-primary/30">{success}</Card>;
  }

  const form = (
    <>
      <CardHeader>
        <CardTitle>Share Your Experience</CardTitle>
        <CardDescription>
          Your feedback helps us improve and helps others make informed decisions. 
          You'll receive a verification email to confirm your review.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {!lockProduct && <div className="space-y-2">
            <Label htmlFor="product">Product (Optional)</Label>
            <Select value={formData.product} onValueChange={(value) => setFormData({ ...formData, product: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General Review</SelectItem>
                <SelectItem value="brainCharge">Brain Charge™</SelectItem>
              </SelectContent>
            </Select>
          </div>}

          <div className="space-y-2">
            <Label>Rating *</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= (hoverRating || rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Review Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Sum up your experience in one line"
              required
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="review">Your Review *</Label>
            <Textarea
              id="review"
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              placeholder="Share your detailed experience with our products or company..."
              required
              maxLength={2000}
              rows={6}
            />
            <p className="text-xs text-muted-foreground">
              {formData.review.length}/2000 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label>Add Photos (Optional)</Label>
            <p className="text-xs text-muted-foreground">
              Show off your results — up to {MAX_PHOTOS} images, max 5MB each.
            </p>
            {photoPreviews.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-2">
                {photoPreviews.map((src, i) => (
                  <div key={src} className="relative w-24 h-24 rounded-md overflow-hidden border">
                    <img src={src} alt={`Upload preview ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-background/90 hover:bg-background rounded-full p-1 shadow"
                      aria-label="Remove photo"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {photoFiles.length < MAX_PHOTOS && (
              <label className="inline-flex items-center gap-2 mt-2 px-4 py-2 border border-dashed rounded-md cursor-pointer hover:bg-accent transition-colors text-sm">
                <Upload className="h-4 w-4" />
                <span>Choose photos</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoChange}
                />
              </label>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Your Name *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required maxLength={100} autoComplete="name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required maxLength={255} autoComplete="email" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">We’ll email a verification link before your review enters moderation.</p>

          <Turnstile onVerify={handleTurnstileVerify} onExpire={handleTurnstileExpire} className="mt-2" />

          <Button type="submit" disabled={loading || uploading || !turnstileToken} className="w-full">
            {uploading ? "Uploading photos..." : loading ? "Submitting..." : "Submit Review"}
          </Button>
        </form>
      </CardContent>
    </>
  );
  return embedded ? <div>{form}</div> : <Card>{form}</Card>;
}
